<div class="process-templates-container">

  <!-- ============================================================
       Left Panel – Process Template List
  ============================================================ -->
  <div class="list-panel">
    <div class="panel-header">
      <h3>Process Templates</h3>
      <button kendoButton themeColor="primary" (click)="createNewTemplate()">
        <span class="k-icon k-font-icon k-i-plus"></span> New
      </button>
    </div>

    <kendo-grid
      [data]="processTemplates"
      [loading]="loading"
      [selectable]="{ mode: 'single' }"
      (selectionChange)="onTemplateSelect($event)"
      class="templates-grid"
    >
      <kendo-grid-column field="name" title="Name"></kendo-grid-column>
      <kendo-grid-column field="description" title="Description"></kendo-grid-column>
    </kendo-grid>
  </div>

  <!-- ============================================================
       Right Panel – Template Detail + Tasks
  ============================================================ -->
  <div class="detail-panel" *ngIf="selectedTemplate || isNewTemplate">

    <!-- ---- Template Header ---- -->
    <div class="panel-header">
      <h3>{{ isNewTemplate ? 'New Process Template' : selectedTemplate?.name }}</h3>
      <div class="header-actions" *ngIf="!isEditingTemplate">
        <button kendoButton (click)="editTemplate()">
          <span class="k-icon k-font-icon k-i-edit"></span> Edit
        </button>
        <button kendoButton themeColor="error" (click)="confirmDeleteTemplate()">
          <span class="k-icon k-font-icon k-i-delete"></span> Delete
        </button>
      </div>
    </div>

    <!-- ---- Template Form ---- -->
    <form *ngIf="isEditingTemplate" [formGroup]="templateForm" class="template-form">
      <div class="form-row">
        <label>Name <span class="required">*</span></label>
        <kendo-textbox formControlName="name" placeholder="Process template name"></kendo-textbox>
        <div class="validation-error" *ngIf="templateForm.get('name')?.invalid && templateForm.get('name')?.touched">
          Name is required (max 50 characters).
        </div>
      </div>

      <div class="form-row">
        <label>Description <span class="required">*</span></label>
        <kendo-textarea formControlName="description" placeholder="Description" [rows]="3"></kendo-textarea>
        <div class="validation-error" *ngIf="templateForm.get('description')?.invalid && templateForm.get('description')?.touched">
          Description is required (max 250 characters).
        </div>
      </div>

      <div class="form-actions">
        <button kendoButton themeColor="primary" (click)="saveTemplate()" [disabled]="templateForm.invalid">Save</button>
        <button kendoButton (click)="cancelEditTemplate()">Cancel</button>
      </div>
    </form>

    <!-- ---- Template Read View ---- -->
    <div class="template-details" *ngIf="!isEditingTemplate && selectedTemplate">
      <div class="detail-row">
        <span class="detail-label">Name</span>
        <span class="detail-value">{{ selectedTemplate.name }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Description</span>
        <span class="detail-value">{{ selectedTemplate.description }}</span>
      </div>
    </div>

    <!-- ---- Task Templates Section (only when not editing the template itself) ---- -->
    <div class="tasks-section" *ngIf="!isEditingTemplate && selectedTemplate">
      <div class="section-header">
        <h4>Task Templates</h4>
        <button kendoButton themeColor="primary" size="small" (click)="createNewTask()">
          <span class="k-icon k-font-icon k-i-plus"></span> Add Task
        </button>
      </div>

      <!-- Task Grid -->
      <kendo-grid
        [data]="tasks"
        [loading]="tasksLoading"
        [selectable]="{ mode: 'single' }"
        (selectionChange)="onTaskSelect($event)"
        class="tasks-grid"
        *ngIf="!isEditingTask"
      >
        <kendo-grid-column field="sequence" title="Seq" [width]="60"></kendo-grid-column>
        <kendo-grid-column field="process_Id" title="Process" [width]="160">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.process?.name ?? dataItem.process_Id }}
          </ng-template>
        </kendo-grid-column>
        <kendo-grid-column field="parentProcess_Id" title="Parent ID" [width]="100">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.parentProcess_Id ?? '—' }}
          </ng-template>
        </kendo-grid-column>
        <kendo-grid-column field="isParallel" title="Parallel" [width]="80">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="k-icon k-font-icon"
              [class.k-i-check-circle]="dataItem.isParallel"
              [class.k-i-close-circle]="!dataItem.isParallel"
              [style.color]="dataItem.isParallel ? 'var(--kendo-color-success, #22c55e)' : 'var(--kendo-color-error, #ef4444)'"
            ></span>
          </ng-template>
        </kendo-grid-column>
        <kendo-grid-column field="isRequired" title="Required" [width]="85">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="k-icon k-font-icon"
              [class.k-i-check-circle]="dataItem.isRequired"
              [class.k-i-close-circle]="!dataItem.isRequired"
              [style.color]="dataItem.isRequired ? 'var(--kendo-color-success, #22c55e)' : 'var(--kendo-color-error, #ef4444)'"
            ></span>
          </ng-template>
        </kendo-grid-column>
        <kendo-grid-column title="Actions" [width]="100">
          <ng-template kendoGridCellTemplate let-dataItem>
            <button kendoButton size="small" fillMode="flat" title="Edit" (click)="editTask(dataItem)">
              <span class="k-icon k-font-icon k-i-edit"></span>
            </button>
            <button kendoButton size="small" fillMode="flat" themeColor="error" title="Delete" (click)="confirmDeleteTask(dataItem)">
              <span class="k-icon k-font-icon k-i-delete"></span>
            </button>
          </ng-template>
        </kendo-grid-column>
      </kendo-grid>

      <!-- Task Form (inline) -->
      <div class="task-form-panel" *ngIf="isEditingTask">
        <h5>{{ isNewTask ? 'New Task Template' : 'Edit Task Template' }}</h5>
        <form [formGroup]="taskForm" class="task-form">

          <div class="form-row-group">
            <div class="form-row">
              <label>Process ID <span class="required">*</span></label>
              <kendo-numerictextbox formControlName="process_Id" [decimals]="0" [format]="'n0'" placeholder="Process ID"></kendo-numerictextbox>
              <div class="validation-error" *ngIf="taskForm.get('process_Id')?.invalid && taskForm.get('process_Id')?.touched">
                Process ID is required.
              </div>
            </div>

            <div class="form-row">
              <label>Sequence <span class="required">*</span></label>
              <kendo-numerictextbox formControlName="sequence" [decimals]="0" [format]="'n0'" [min]="1"></kendo-numerictextbox>
            </div>
          </div>

          <div class="form-row">
            <label>Parent Process ID</label>
            <kendo-numerictextbox formControlName="parentProcess_Id" [decimals]="0" [format]="'n0'" placeholder="Optional"></kendo-numerictextbox>
          </div>

          <div class="form-row-group">
            <div class="form-row checkbox-row">
              <kendo-switch formControlName="isParallel"></kendo-switch>
              <label>Is Parallel</label>
            </div>

            <div class="form-row checkbox-row">
              <kendo-switch formControlName="isRequired"></kendo-switch>
              <label>Is Required</label>
            </div>
          </div>

          <div class="form-actions">
            <button kendoButton themeColor="primary" (click)="saveTask()" [disabled]="taskForm.invalid">Save</button>
            <button kendoButton (click)="cancelEditTask()">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Empty state -->
  <div class="empty-state" *ngIf="!selectedTemplate && !isNewTemplate">
    <span class="k-icon k-font-icon k-i-track-changes-enable empty-icon"></span>
    <p>Select a process template or create a new one.</p>
  </div>
</div>

<!-- ============================================================
     Delete Template Confirmation Dialog
============================================================ -->
<kendo-dialog
  title="Delete Process Template"
  *ngIf="deleteTemplateDialogOpen"
  (close)="cancelDeleteTemplate()"
  [minWidth]="300"
  [width]="440"
>
  <p>Are you sure you want to delete <strong>{{ selectedTemplate?.name }}</strong>?
    This will also remove all associated task templates.</p>
  <kendo-dialog-actions>
    <button kendoButton (click)="cancelDeleteTemplate()">Cancel</button>
    <button kendoButton themeColor="error" (click)="deleteTemplate()">Delete</button>
  </kendo-dialog-actions>
</kendo-dialog>

<!-- ============================================================
     Delete Task Confirmation Dialog
============================================================ -->
<kendo-dialog
  title="Delete Task Template"
  *ngIf="deleteTaskDialogOpen"
  (close)="cancelDeleteTask()"
  [minWidth]="300"
  [width]="440"
>
  <p>Are you sure you want to delete this task template (Process: {{ selectedTask?.process?.name ?? selectedTask?.process_Id }})?</p>
  <kendo-dialog-actions>
    <button kendoButton (click)="cancelDeleteTask()">Cancel</button>
    <button kendoButton themeColor="error" (click)="deleteTask()">Delete</button>
  </kendo-dialog-actions>
</kendo-dialog>
